import { Box, Grid, Stack, Typography } from '@mui/material';
import React from 'react';
import sanitizeHTML from '../../services/sanitizeHTML';
import AssetImage from '../AssetImage';
import { StyledTypography } from '../styles/styles';
import { ChangeType, ContentDiffEntry } from './contentDiffTypes';

export const CHANGE_COLORS: Record<ChangeType, { bg: string; text: string }> = {
  added: { bg: 'success.light', text: 'success.main' },
  removed: { bg: 'error.light', text: 'error.main' },
  changed: { bg: 'info.light', text: 'info.main' },
};

const HTML_BLOCK_PATH_MATCHERS: Record<string, RegExp[]> = {
  StandardHeaderText: [
    /\.properties\.content.*\.content$/i,
    /\.properties\.footnotes\[\d+\]\.content$/i,
  ],
  LeftRightList: [
    /\.properties\.items.*\.text.*\.content$/i,
    /\.properties\.footnotes\[\d+\]\.content$/i,
  ],
  ImageCarousel: [
    /\.properties\.items.*\.text\.content$/i,
    /\.properties\.footnotes\[\d+\]\.content$/i,
  ],
  Table: [
    /\.properties\.rows.*\.cells.*\.content\.content$/i,
    /\.properties\.footnotes\[\d+\]\.content$/i,
  ],
  ChartData: [/\.footnotes\[\d+\]\.content$/i],
};

const renderComplexValue = (value: any) => (
  <Box
    component="pre"
    sx={{
      fontFamily: 'monospace',
      fontSize: '0.75rem',
      backgroundColor: 'rgba(0,0,0,0.04)',
      borderRadius: 1,
      p: 1,
      m: 0,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}
  >
    {JSON.stringify(value, null, 2)}
  </Box>
);

const renderHtmlValue = (value: string) => {
  const safeHtml = sanitizeHTML(value);

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            fontSize: '0.9rem',
            lineHeight: 1.4,
            wordBreak: 'break-word',
            '& p': { m: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
      </Box>
      <StyledTypography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {value}
      </StyledTypography>
    </Stack>
  );
};

const pathMatchesHtmlRule = (blockType: string | null | undefined, path: string) => {
  if (!blockType) return false;
  const rules = HTML_BLOCK_PATH_MATCHERS[blockType];
  if (!rules) return false;
  return rules.some((regex) => regex.test(path));
};

const isLikelyHtmlString = (value: string) => /<[^>]+>/.test(value);

export const shouldRenderAsHtml = (entry: ContentDiffEntry, value: any): value is string => {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }

  if (pathMatchesHtmlRule(entry.blockType ?? undefined, entry.path ?? '')) {
    return true;
  }

  return isLikelyHtmlString(value);
};

export const renderValue = (entry: ContentDiffEntry, key: 'before' | 'after') => {
  const value = entry[key];
  const valueType = entry.valueType;

  if (value === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  if (valueType === 'string' && typeof value === 'string') {
    if (shouldRenderAsHtml(entry, value)) {
      return renderHtmlValue(value);
    }
    return (
      <StyledTypography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {value}
      </StyledTypography>
    );
  }

  if (valueType === 'number' || valueType === 'boolean') {
    return (
      <StyledTypography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {String(value)}
      </StyledTypography>
    );
  }

  return renderComplexValue(value);
};

const renderFormattedDiff = (entry: ContentDiffEntry) => {
  const beforeHtml = entry.before || '';
  const afterHtml = entry.after || '';

  return (
    <Stack spacing={1}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2,
              border: '2px solid',
              borderColor: 'error.light',
              borderRadius: 1,
              bgcolor: 'error.lighter',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                color: 'error.dark',
                fontWeight: 'bold',
              }}
            >
              Before
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.9rem',
                lineHeight: 1.4,
                wordBreak: 'break-word',
                '& p': { m: 0 },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(beforeHtml) }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2,
              border: '2px solid',
              borderColor: 'success.light',
              borderRadius: 1,
              bgcolor: 'success.lighter',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                color: 'success.dark',
                fontWeight: 'bold',
              }}
            >
              After
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.9rem',
                lineHeight: 1.4,
                wordBreak: 'break-word',
                '& p': { m: 0 },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(afterHtml) }}
            />
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};

export const renderInlineDiff = (entry: ContentDiffEntry) => {
  const { prefix, removed, added, suffix } = entry.inlineDiff!;
  const showHtmlPreview =
    shouldRenderAsHtml(entry, entry.before) || shouldRenderAsHtml(entry, entry.after);

  if (showHtmlPreview) {
    return renderFormattedDiff(entry);
  }

  const isImageURL = entry.path.match(/.imageUrl.url$/);

  return (
    <Stack spacing={1}>
      <StyledTypography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {!isImageURL && prefix && <span>{prefix}</span>}
        {isImageURL && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssetImage url={entry.before} sx={{ maxWidth: 64, maxHeight: 64 }} />
            <Box component="span" sx={{ mx: 1, color: 'text.secondary', fontWeight: 600 }}>
              &rarr;
            </Box>
            <AssetImage url={entry.after} sx={{ maxWidth: 64, maxHeight: 64 }} />
          </Box>
        )}
        {!isImageURL && removed && (
          <Box
            component="span"
            sx={{
              textDecoration: 'line-through',
              color: '#d32f2f',
              backgroundColor: '#ffebee',
              padding: '1px 2px',
              borderRadius: '2px',
            }}
          >
            {removed}
          </Box>
        )}
        {!isImageURL && added && (
          <Box
            component="span"
            sx={{
              color: '#2e7d32',
              fontWeight: 600,
              backgroundColor: '#e8f5e8',
              padding: '1px 2px',
              borderRadius: '2px',
            }}
          >
            {added}
          </Box>
        )}
        {!isImageURL && suffix && <span>{suffix}</span>}
      </StyledTypography>
    </Stack>
  );
};
